/** STILUL FAȚADEI SOLUȚIEI DESCHISE și ANULAREA schimbărilor făcute pe ea.
 *
 *  Sursa e soluția, nu proiectul (Cătălin, 6.10.2026): panoul și asistentul
 *  lucrează pe ce vede omul în machetă. Agentul scrie stilul pe document
 *  (`diagnostics.fatada.stil`, cu revizia lui), iar o soluție îmbrăcată numai
 *  la generare îl poartă pe fețe (`Facade.properties.style`).
 *
 *  ANULAREA ȘI REFACEREA țin NUMAI ÎN MEMORIA FERESTREI, cât stai pe pagină:
 *  nimic în bază, nimic în `localStorage`. Se pierd la reîncărcare, la ieșirea
 *  din vizualizator, la altă soluție sau alt proiect (`golesteTot`). Fiecare
 *  pas ține starea DE DINAINTE (stilul și compoziția) și revizia scrisă de
 *  schimbare: anularea scrie starea înapoi numai dacă revizia de pe soluție e
 *  încă aceea — altfel a scris altcineva între timp și stivele se golesc.
 *
 *  Anularea face un pas de REFACERE cu starea de după (13.10.2026: butonul din
 *  discuție se întoarce în «refă», iar omul se plimbă între cele două fațade).
 *  O schimbare nouă golește refacerile.
 *
 *  INSTANTANEELE: documentul machetei de dinainte și de după, ținut ca să se
 *  pună pe ecran pe loc, fără reîncărcare și fără să miște camera; scrierea pe
 *  soluție merge în urma lui. Un document are zeci de MB, deci instantaneul
 *  stă numai pe pasul de DEASUPRA fiecărei stive — cei de dedesubt se anulează
 *  prin server, cu documentul recitit. */
import type { StilFatada } from "../../api";
import type { CanonicalDoc } from "../canonical";

export interface StareFatada {
  /** Stilul aplicat pe soluție; `null` = nu se știe (document fără fețe). */
  stil: StilFatada | null;
  /** Revizia fațadei de pe document (0 = n-a scris nimeni după generare). */
  revizie: number;
  /** Secțiunea de compoziție (balcoane, goluri, vitraje); `{}` = clădirea din șablon. */
  sectiune: Record<string, unknown>;
}

/** Ce poartă documentul despre fațada lui. */
export function stareaFatadei(doc: CanonicalDoc | null): StareFatada {
  const diag = (doc?.diagnostics ?? {}) as Record<string, any>;
  const fatada = diag.fatada ?? {};
  const revizie = Number(fatada.revizie ?? 0) || 0;
  const sectiune = (diag.compozitie?.sectiune ?? {}) as Record<string, unknown>;
  let stil: StilFatada | null = fatada.stil ?? null;
  if (!stil) {
    // GENERATĂ, neatinsă de agent: stilul cu care s-a emis stă pe fețele
    // principale (cele ale corpului, nu ale teraselor)
    for (const o of doc?.objects ?? []) {
      const st = (o.properties as { style?: StilFatada } | undefined)?.style;
      if (o.type === "Facade" && st && Array.isArray(st.rules)) { stil = st; break; }
    }
  }
  return { stil, revizie, sectiune };
}

/** BALUSTRADELE CONSTRUITE pe soluție, numărate: ce vede omul în machetă, nu
 *  ce scrie în reguli. Fără ele asistentul putea spune «nu sunt balustrade de
 *  sticlă» despre ce omul vede ca sticlă (Demo-UTR6, 6.10.2026); cu ele știe
 *  felul, materialul, culoarea și regula care a pus fiecare grup. */
export function balustradele(doc: CanonicalDoc | null): { fel: string; material: string | null;
    culoare: string | null; unde: string | null; regula: string | null; bucati: number }[] {
  const grupe = new Map<string, { fel: string; material: string | null; culoare: string | null;
                                  unde: string | null; regula: string | null; bucati: number }>();
  for (const o of doc?.objects ?? []) {
    if (o.type !== "Railing") continue;
    const p = (o.properties ?? {}) as Record<string, unknown>;
    const r = ((o.props as Record<string, Record<string, unknown>> | undefined)?.["architecture.railing"]) ?? {};
    const g = { fel: String(p.kind ?? "?"), material: (p.material as string) ?? null,
                culoare: (p.colour as string) ?? null, unde: (r.outdoor as string) ?? null,
                regula: (r.rule as string) ?? null, bucati: 0 };
    const k = [g.fel, g.material, g.culoare, g.unde, g.regula].join("|");
    (grupe.get(k) ?? grupe.set(k, g).get(k)!).bucati += 1;
  }
  return [...grupe.values()].sort((a, b) => b.bucati - a.bucati);
}

/** Un pas de anulat: starea de dinainte și revizia lăsată de schimbare. */
export interface PasAnulare {
  stil: StilFatada | null;
  sectiune: Record<string, unknown>;
  /** Revizia scrisă de schimbare; anularea cere s-o găsească pe soluție.
   *  `-1` = a agentului, încă nescrisă (se află după ce termină). */
  revizieDupa: number;
  /** Revizia de dinainte; la pasul agentului spune dacă el a scris ceva. */
  revizieInainte?: number;
  /** Ce s-a schimbat, pe scurt, în limba omului (pentru asistent). */
  ce: string;
  /** Documentul machetei DE DINAINTE (opac: îl dă și îl ia vizualizatorul). */
  doc?: unknown;
}

/** Un pas de refăcut: starea DE DUPĂ schimbarea anulată și revizia lăsată de
 *  anulare (refacerea cere s-o găsească pe soluție). */
export interface PasRefacere {
  stil: StilFatada | null;
  sectiune: Record<string, unknown>;
  revizieDupa: number;
  ce: string;
  /** Documentul machetei DE DUPĂ (cel de pe ecran înainte de anulare). */
  doc?: unknown;
}

const MAX_PASI = 20;
let proiectul: string | null = null;
const stive = new Map<string, PasAnulare[]>();
const refaceri = new Map<string, PasRefacere[]>();

/** Instantaneul numai pe vârf: cei de dedesubt își lasă documentul. */
function numaiVarful(s: { doc?: unknown }[]): void {
  for (let i = 0; i < s.length - 1; i++) delete s[i].doc;
}

/** Stiva unei soluții. Alt proiect golește tot: anularea nu iese din proiect. */
function stiva(projectId: string, solutionId: string): PasAnulare[] {
  if (proiectul !== projectId) { golesteTot(); proiectul = projectId; }
  let s = stive.get(solutionId);
  if (!s) { s = []; stive.set(solutionId, s); }
  return s;
}

function stivaRefacerii(projectId: string, solutionId: string): PasRefacere[] {
  stiva(projectId, solutionId);
  let s = refaceri.get(solutionId);
  if (!s) { s = []; refaceri.set(solutionId, s); }
  return s;
}

/** O SCHIMBARE NOUĂ: pasul ei de anulat, iar refacerile pleacă. */
export function tinePas(projectId: string, solutionId: string, pas: PasAnulare): void {
  const s = stiva(projectId, solutionId);
  s.push(pas);
  if (s.length > MAX_PASI) s.splice(0, s.length - MAX_PASI);
  numaiVarful(s);
  stivaRefacerii(projectId, solutionId).length = 0;
}

/** Pasul pus înapoi de o REFACERE: ca `tinePas`, dar refacerile de sub el rămân. */
export function punePasInapoi(projectId: string, solutionId: string, pas: PasAnulare): void {
  const s = stiva(projectId, solutionId);
  s.push(pas);
  if (s.length > MAX_PASI) s.splice(0, s.length - MAX_PASI);
  numaiVarful(s);
}

export function tineRefacere(projectId: string, solutionId: string, pas: PasRefacere): void {
  const s = stivaRefacerii(projectId, solutionId);
  s.push(pas);
  if (s.length > MAX_PASI) s.splice(0, s.length - MAX_PASI);
  numaiVarful(s);
}

export function ultimaRefacere(projectId: string, solutionId: string): PasRefacere | null {
  const s = stivaRefacerii(projectId, solutionId);
  return s.length ? s[s.length - 1] : null;
}

export function scoateRefacere(projectId: string, solutionId: string): PasRefacere | null {
  return stivaRefacerii(projectId, solutionId).pop() ?? null;
}

export function cateRefaceri(projectId: string, solutionId: string): number {
  return stivaRefacerii(projectId, solutionId).length;
}

/** Totul afară din memorie: omul a ieșit din vizualizator sau a schimbat soluția. */
export function golesteTot(): void {
  stive.clear();
  refaceri.clear();
}

export function ultimulPas(projectId: string, solutionId: string): PasAnulare | null {
  const s = stiva(projectId, solutionId);
  return s.length ? s[s.length - 1] : null;
}

export function scoatePas(projectId: string, solutionId: string): PasAnulare | null {
  return stiva(projectId, solutionId).pop() ?? null;
}

/** Ambele stive ale soluției (a scris altcineva între timp). */
export function golesteStiva(projectId: string, solutionId: string): void {
  stiva(projectId, solutionId).length = 0;
  stivaRefacerii(projectId, solutionId).length = 0;
}

export function cativaPasi(projectId: string, solutionId: string): number {
  return stiva(projectId, solutionId).length;
}

/** Aceeași compoziție? (cheile în orice ordine) */
export function aceeasiSectiune(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const canon = (v: unknown): unknown => Array.isArray(v) ? v.map(canon)
    : v && typeof v === "object"
      ? Object.fromEntries(Object.keys(v as object).sort().map((k) => [k, canon((v as any)[k])]))
      : v;
  const gol = (s: Record<string, unknown>) => Object.values(s).every((x) => x == null
    || (Array.isArray(x) && x.length === 0));
  if (gol(a) && gol(b)) return true;
  return JSON.stringify(canon(a)) === JSON.stringify(canon(b));
}
